import { useMode } from '@/contexts/ModeContext';
import useTranslation from '@/hooks/useTranslation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useWeather } from '@/hooks/useWeather';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { 
  Droplet, 
  Sprout, 
  Home, 
  AlertTriangle, 
  Download,
  Share2,
  Calendar,
  Bell
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import React from 'react';

interface WeatherData {
  temperature: number;
  humidity: number;
}

interface WeatherAnalysisProps {
  weatherData: WeatherData;
}

export const WeatherAnalysis: React.FC<WeatherAnalysisProps> = ({ weatherData }) => {
  return (
    <div>
      <h2>Weather Analysis</h2>
      <p>Temperature: {weatherData.temperature}°C</p>
      <p>Humidity: {weatherData.humidity}%</p>
    </div>
  );
};

export function AIInsights() {
  const { mode, isAgriculture } = useMode();
  const { t } = useTranslation();
  const { weatherData, isLoading, error } = useWeather();
  const [activeTab, setActiveTab] = useState('tips');

  const agricultureTips = [
    {
      title: t('insights.agricultureTips.irrigation'),
      description: 'Water early morning or late evening to minimize evaporation. Use weather data to optimize irrigation timing.',
      icon: <Sprout className="h-6 w-6" />,
      actions: [
        { label: 'Set Schedule', icon: <Calendar className="h-4 w-4" /> },
        { label: 'Set Alert', icon: <Bell className="h-4 w-4" /> }
      ]
    },
    {
      title: t('insights.agricultureTips.soil'),
      description: 'Maintain optimal soil moisture levels by monitoring weather conditions and adjusting irrigation accordingly.',
      icon: <Droplet className="h-6 w-6" />,
      actions: [
        { label: 'View Details', icon: <Share2 className="h-4 w-4" /> },
        { label: 'Download Data', icon: <Download className="h-4 w-4" /> }
      ]
    },
    // Add more agriculture-specific tips
  ];

  const householdTips = [
    {
      title: t('insights.householdTips.daily'),
      description: 'Fix leaky faucets, take shorter showers, and use water-efficient appliances to reduce household water consumption.',
      icon: <Home className="h-6 w-6" />,
      actions: [
        { label: 'View Guide', icon: <Share2 className="h-4 w-4" /> },
        { label: 'Set Reminder', icon: <Bell className="h-4 w-4" /> }
      ]
    },
    {
      title: t('insights.householdTips.smart'),
      description: 'Collect rainwater for plants, reuse greywater when possible, and install water-saving fixtures.',
      icon: <Droplet className="h-6 w-6" />,
      actions: [
        { label: 'Learn More', icon: <Share2 className="h-4 w-4" /> },
        { label: 'Get Alerts', icon: <Bell className="h-4 w-4" /> }
      ]
    },
    // Add more household-specific tips
  ];

  const tips = isAgriculture ? agricultureTips : householdTips;

  const handleExport = async () => {
    if (!weatherData) return;

    try {
      const report = {
        date: new Date().toISOString(),
        weather: weatherData,
        recommendations: isAgriculture 
          ? t('insights.agricultureTips')
          : t('insights.householdTips'),
        mode: isAgriculture ? 'agriculture' : 'household'
      };

      const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `water-insights-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(t('insights.exportSuccess'));
    } catch (error) {
      toast.error(t('insights.exportError'));
    }
  };

  const handleShare = async () => {
    if (!weatherData) return;

    try {
      if (navigator.share) {
        await navigator.share({
          title: t('insights.shareTitle'),
          text: t('insights.shareText'),
          url: window.location.href
        });
        toast.success(t('insights.shareSuccess'));
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success(t('insights.copySuccess'));
      }
    } catch (error) {
      toast.error(t('insights.shareError'));
    }
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t('insights.title')}
          </h1>
          <p className="text-muted-foreground">
            {isAgriculture 
              ? t('insights.agricultureDescription')
              : t('insights.householdDescription')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExport}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4 mr-2" />
            {t('insights.exportReport')}
          </Button>
          <Button
            variant="outline"
            onClick={handleShare}
            className="flex items-center gap-2"
          >
            <Share2 className="h-4 w-4 mr-2" />
            {t('insights.shareInsights')}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="tips">{t('insights.waterTips')}</TabsTrigger>
          <TabsTrigger value="analysis">{t('insights.analysis')}</TabsTrigger>
        </TabsList>

        <TabsContent value="tips" className="space-y-4">
          <ScrollArea className="h-[600px] rounded-md border p-4">
            {tips.map((tip, index) => (
              <Card key={index} className="mb-4">
                <CardHeader className="flex flex-row items-center space-x-4">
                  {tip.icon}
                  <CardTitle>{tip.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">{tip.description}</p>
                  <div className="flex gap-2">
                    {tip.actions.map((action, actionIndex) => (
                      <Button
                        key={actionIndex}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        {action.icon}
                        {action.label}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </ScrollArea>
        </TabsContent>

        <TabsContent value="analysis">
          {isLoading ? (
            <Card>
              <CardContent className="p-6">
                <p>{t('common.loading')}</p>
              </CardContent>
            </Card>
          ) : weatherData ? (
            <WeatherAnalysis weatherData={weatherData} />
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}